import React, { forwardRef } from "react";
import {
  MDXEditor,
  headingsPlugin,
  listsPlugin,
  linkPlugin,
  quotePlugin,
  markdownShortcutPlugin,
  thematicBreakPlugin,
  tablePlugin,
  toolbarPlugin,
  BoldItalicUnderlineToggles,
  UndoRedo,
  Separator,
  ListsToggle,
  BlockTypeSelect,
  CreateLink,
  InsertThematicBreak,
  InsertTable,
  type MDXEditorMethods,
  type MDXEditorProps
} from "@mdxeditor/editor";
import "@mdxeditor/editor/style.css";
import { cn } from "@/lib/utils";

// Функция перевода совместимая с i18next, как в существующем wysiwyg-editor
const translateToRussian = (key: string, defaultValue: string, interpolations: Record<string, unknown> = {}) => {
  // Используем существующие переводы из wysiwyg-editor.tsx
  const officialRussianTranslations = {
    frontmatterEditor: {
      title: "Редактировать метаданные документа",
      key: "Ключ",
      value: "Значение",
      addEntry: "Добавить запись"
    },
    dialogControls: {
      save: "Сохранить",
      cancel: "Отмена"
    },
    table: {
      deleteTable: "Удалить таблицу",
      columnMenu: "Меню столбца",
      textAlignment: "Выравнивание текста",
      alignLeft: "По левому краю",
      alignCenter: "По центру",
      alignRight: "По правому краю",
      insertColumnLeft: "Вставить столбец слева",
      insertColumnRight: "Вставить столбец справа",
      deleteColumn: "Удалить этот столбец",
      rowMenu: "Меню строки",
      insertRowAbove: "Вставить строку выше",
      insertRowBelow: "Вставить строку ниже",
      deleteRow: "Удалить эту строку"
    },
    toolbar: {
      blockTypes: {
        paragraph: "Абзац",
        quote: "Цитата",
        heading: "Заголовок {{level}}"
      },
      blockTypeSelect: {
        selectBlockTypeTooltip: "Выбрать тип блока",
        placeholder: "Тип блока"
      },
      toggleGroup: "Группа переключателей",
      removeBold: "Убрать жирный",
      bold: "Жирный",
      removeItalic: "Убрать курсив",
      italic: "Курсив",
      underline: "Подчеркнутый",
      removeUnderline: "Убрать подчеркивание",
      link: "Создать ссылку",
      bulletedList: "Маркированный список",
      numberedList: "Нумерованный список",
      table: "Вставить таблицу",
      insertTable: "Вставить таблицу",
      thematicBreak: "Вставить тематический разделитель",
      undo: "Отменить {{shortcut}}",
      redo: "Вернуть {{shortcut}}"
    }
  };

  const keys = key.split('.');
  let value: unknown = officialRussianTranslations;

  for (const k of keys) {
    if (value && typeof value === 'object' && k in value) {
      value = value[k];
    } else {
      value = defaultValue;
      break;
    }
  }

  if (typeof value !== 'string') {
    value = defaultValue;
  }

  for (const [interpKey, interpValue] of Object.entries(interpolations)) {
    value = value.replaceAll(`{{${interpKey}}}`, String(interpValue));
  }

  return value;
};

export interface UnifiedMarkdownRendererProps {
  value: string;
  onChange?: (value: string) => void;
  readOnly?: boolean;
  placeholder?: string;
  height?: number;
  className?: string;
  onKeyDown?: (e: React.KeyboardEvent) => void;
  // Режим: "view" - только просмотр (без тулбара), "edit" - полное редактирование
  mode?: "view" | "edit";
  // Принудительный ключ для обновления компонента при загрузке данных
  forceUpdateKey?: string | number;
}

export const UnifiedMarkdownRenderer = forwardRef<MDXEditorMethods, UnifiedMarkdownRendererProps>(
  (
    {
      value,
      onChange,
      readOnly = false,
      placeholder = "Введите текст...",
      height = 400,
      className,
      onKeyDown,
      mode = "edit",
      forceUpdateKey,
    },
    ref
  ) => {
    const handleChange = (markdown: string) => {
      onChange?.(markdown);
    };

    // Для режима просмотра используем минимальный набор плагинов без тулбара
    const viewModePlugins = [
      headingsPlugin(),
      listsPlugin(),
      linkPlugin(),
      quotePlugin(),
      markdownShortcutPlugin(),
      thematicBreakPlugin(),
      tablePlugin(),
    ];

    // Для режима редактирования добавляем тулбар
    const editModePlugins = [
      ...viewModePlugins,
      toolbarPlugin({
        toolbarContents: () => (
          <>
            <UndoRedo />
            <Separator />
            <BoldItalicUnderlineToggles />
            <Separator />
            <BlockTypeSelect />
            <Separator />
            <ListsToggle />
            <Separator />
            <CreateLink />
            <Separator />
            <InsertTable />
            <Separator />
            <InsertThematicBreak />
          </>
        )
      })
    ];

    const plugins = mode === "view" ? viewModePlugins : editModePlugins;
    const isReadOnly = readOnly || mode === "view";

    return (
      <div
        className={cn(
          "unified-markdown-renderer-wrapper w-full min-h-fit",
          mode === "view" ? "view-mode" : "edit-mode",
          className
        )}
        onKeyDown={onKeyDown}
        style={{ minHeight: height }}
      >
        <MDXEditor
          key={`mdx-editor-${mode}${forceUpdateKey ? `-${forceUpdateKey}` : ''}`}
          ref={ref}
          markdown={value}
          onChange={handleChange}
          placeholder={placeholder}
          readOnly={isReadOnly}
          translation={translateToRussian}
          plugins={plugins}
          suppressHtmlProcessing={true}
          className={cn(
            "!border-border !rounded-md",
            "focus-within:!border-brand-500",
            isReadOnly && "!opacity-100", // Не делаем прозрачным в режиме просмотра
            mode === "view" && "!border-none !shadow-none" // Убираем границы в режиме просмотра
          )}
          contentEditableClassName={cn(
            "prose prose-sm max-w-none",
            "!text-foreground !bg-background",
            "focus:!outline-none",
            "px-4 py-2",
            // Стили заголовков
            "[&_h1]:!text-foreground [&_h1]:!text-2xl [&_h1]:!font-bold [&_h1]:!mt-6 [&_h1]:!mb-4",
            "[&_h2]:!text-foreground [&_h2]:!text-xl [&_h2]:!font-bold [&_h2]:!mt-5 [&_h2]:!mb-3",
            "[&_h3]:!text-foreground [&_h3]:!text-lg [&_h3]:!font-bold [&_h3]:!mt-4 [&_h3]:!mb-2",
            // Стили текста
            "[&_p]:!text-foreground [&_p]:!text-sm [&_p]:!leading-relaxed [&_p]:!mb-3",
            "[&_ul]:!text-foreground [&_ul]:!text-sm [&_ul]:!mb-3",
            "[&_ol]:!text-foreground [&_ol]:!text-sm [&_ol]:!mb-3",
            "[&_li]:!text-foreground [&_li]:!text-sm [&_li]:!mb-1",
            // Стили цитат
            "[&_blockquote]:!text-muted-foreground [&_blockquote]:!border-l-border [&_blockquote]:!bg-muted/50 [&_blockquote]:!px-4 [&_blockquote]:!py-2 [&_blockquote]:!rounded-r",
            // Стили ссылок
            "[&_a]:!text-brand-600 [&_a]:!underline hover:[&_a]:!text-brand-700",
            // Стили форматирования
            "[&_strong]:!text-foreground [&_strong]:!font-bold",
            "[&_em]:!text-foreground [&_em]:!italic",
            "[&_code]:!text-foreground [&_code]:!bg-muted [&_code]:!px-1 [&_code]:!py-0.5 [&_code]:!rounded [&_code]:!text-xs",
            // Стили таблиц (ключевое отличие!)
            "[&_table]:!border-collapse [&_table]:!border [&_table]:!border-border [&_table]:!my-4 [&_table]:!min-w-full",
            "[&_thead]:!bg-muted/50",
            "[&_th]:!border [&_th]:!border-border [&_th]:!px-4 [&_th]:!py-2 [&_th]:!text-left [&_th]:!font-semibold [&_th]:!text-foreground",
            "[&_td]:!border [&_td]:!border-border [&_td]:!px-4 [&_td]:!py-2 [&_td]:!text-foreground",
            "[&_tr]:!border-b [&_tr]:!border-border",
            // Состояние
            isReadOnly && mode === "view" && "!pointer-events-auto !cursor-text",
            isReadOnly && mode !== "view" && "!pointer-events-none"
          )}
        />
        <style>{`
          .unified-markdown-renderer-wrapper .mdxeditor {
            border-radius: 6px !important;
            background-color: hsl(var(--background)) !important;
            height: auto !important;
            width: 100% !important;
            display: block !important;
            position: relative !important;
          }

          /* Стили для режима просмотра */
          .unified-markdown-renderer-wrapper.view-mode .mdxeditor {
            border: none !important;
            min-height: ${height}px !important;
          }

          /* Стили для режима редактирования */
          .unified-markdown-renderer-wrapper.edit-mode .mdxeditor {
            border: 1px solid hsl(var(--border)) !important;
            min-height: ${height - 50}px !important;
          }

          /* Тулбар скрыт в режиме просмотра */
          .unified-markdown-renderer-wrapper.view-mode .mdxeditor-toolbar {
            display: none !important;
          }

          /* Тулбар отображается в режиме редактирования */
          .unified-markdown-renderer-wrapper.edit-mode .mdxeditor-toolbar {
            background-color: hsl(var(--background)) !important;
            border-bottom: 1px solid hsl(var(--border)) !important;
            border-radius: 6px 6px 0 0 !important;
            padding: 8px 12px !important;
            position: sticky !important;
            top: 64px !important;
            z-index: 50 !important;
            background-color: hsl(var(--background) / 0.95) !important;
            backdrop-filter: blur(8px) !important;
          }

          /* Стили для кнопок тулбара в режиме редактирования */
          .unified-markdown-renderer-wrapper .mdxeditor-toolbar button {
            color: hsl(var(--foreground)) !important;
            border: none !important;
            background: transparent !important;
            border-radius: 4px !important;
            padding: 6px 8px !important;
            transition: all 150ms ease !important;
          }

          .unified-markdown-renderer-wrapper .mdxeditor-toolbar button:hover {
            color: hsl(var(--foreground)) !important;
            background-color: hsl(var(--muted)) !important;
          }

          .unified-markdown-renderer-wrapper .mdxeditor-toolbar button[data-state="on"] {
            color: hsl(var(--primary)) !important;
            background-color: hsl(var(--accent)) !important;
            border: 1px solid hsl(var(--accent)) !important;
          }

          .unified-markdown-renderer-wrapper .mdxeditor-toolbar button[aria-pressed="true"] {
            color: hsl(var(--primary)) !important;
            background-color: hsl(var(--accent)) !important;
            border: 1px solid hsl(var(--accent)) !important;
          }

          /* Стили для select элементов (BlockTypeSelect) */
          .unified-markdown-renderer-wrapper .mdxeditor-toolbar select {
            color: hsl(var(--foreground)) !important;
            background-color: hsl(var(--background)) !important;
            border: 1px solid hsl(var(--border)) !important;
            border-radius: 4px !important;
            padding: 4px 8px !important;
            font-size: 14px !important;
          }

          .unified-markdown-renderer-wrapper .mdxeditor-toolbar select:focus {
            outline: none !important;
            border-color: hsl(var(--ring)) !important;
            box-shadow: 0 0 0 2px hsl(var(--ring) / 0.2) !important;
          }

          /* Стили для разделителей */
          .unified-markdown-renderer-wrapper .mdxeditor-toolbar [role="separator"] {
            background-color: hsl(var(--border)) !important;
          }

          /* Стили для SVG иконок */
          .unified-markdown-renderer-wrapper .mdxeditor-toolbar svg {
            color: hsl(var(--foreground)) !important;
          }

          .unified-markdown-renderer-wrapper .mdxeditor-rich-text-editor {
            background-color: hsl(var(--background)) !important;
            height: auto !important;
            overflow-y: auto !important;
            width: 100% !important;
            display: block !important;
            position: relative !important;
          }

          /* Rich text editor в режиме просмотра */
          .unified-markdown-renderer-wrapper.view-mode .mdxeditor-rich-text-editor {
            border-radius: 6px !important;
            border: none !important;
            min-height: ${height}px !important;
            cursor: text !important;
          }

          .unified-markdown-renderer-wrapper.view-mode .mdxeditor-rich-text-editor * {
            cursor: text !important;
          }

          /* Rich text editor в режиме редактирования */
          .unified-markdown-renderer-wrapper.edit-mode .mdxeditor-rich-text-editor {
            border-radius: 0 0 6px 6px !important;
            min-height: ${height - 50}px !important;
          }

          .unified-markdown-renderer-wrapper.edit-mode .mdxeditor-rich-text-editor:focus-within {
            border-color: hsl(var(--ring)) !important;
            box-shadow: 0 0 0 2px hsl(var(--ring) / 0.2) !important;
          }

          /* Мобильная оптимизация */
          @media (max-width: 768px) {
            .unified-markdown-renderer-wrapper.view-mode .mdxeditor-toolbar {
              display: none !important;
            }

            .unified-markdown-renderer-wrapper.edit-mode .mdxeditor-toolbar {
              padding: 6px 8px !important;
              display: flex !important;
              flex-wrap: nowrap !important;
              overflow-x: auto !important;
              overflow-y: hidden !important;
              background-color: hsl(var(--background) / 0.95) !important;
              position: sticky !important;
              top: 64px !important;
              z-index: 50 !important;
              backdrop-filter: blur(8px) !important;
              white-space: nowrap !important;
              -webkit-overflow-scrolling: touch !important;
              scrollbar-width: thin !important;
              scrollbar-color: hsl(var(--border)) transparent !important;
            }

            /* Стили для скроллбара тулбара на мобильных */
            .unified-markdown-renderer-wrapper .mdxeditor-toolbar::-webkit-scrollbar {
              height: 6px !important;
            }

            .unified-markdown-renderer-wrapper .mdxeditor-toolbar::-webkit-scrollbar-track {
              background: transparent !important;
            }

            .unified-markdown-renderer-wrapper .mdxeditor-toolbar::-webkit-scrollbar-thumb {
              background-color: hsl(var(--border)) !important;
              border-radius: 3px !important;
            }

            .unified-markdown-renderer-wrapper .mdxeditor-toolbar::-webkit-scrollbar-thumb:hover {
              background-color: hsl(var(--muted-foreground)) !important;
            }

            /* Мобильные стили для кнопок */
            .unified-markdown-renderer-wrapper .mdxeditor-toolbar button {
              padding: 8px !important;
              margin: 2px !important;
              min-width: 36px !important;
              min-height: 36px !important;
              color: hsl(var(--foreground)) !important;
              flex-shrink: 0 !important;
              white-space: nowrap !important;
            }

            .unified-markdown-renderer-wrapper .mdxeditor-toolbar button:hover {
              background-color: hsl(var(--muted)) !important;
            }

            .unified-markdown-renderer-wrapper .mdxeditor-toolbar button[data-state="on"] {
              color: hsl(var(--primary)) !important;
              background-color: hsl(var(--accent)) !important;
              border: 1px solid hsl(var(--accent)) !important;
            }

            /* Мобильные стили для select */
            .unified-markdown-renderer-wrapper .mdxeditor-toolbar select {
              flex-shrink: 0 !important;
              min-width: 100px !important;
            }

            /* Мобильные стили для разделителей */
            .unified-markdown-renderer-wrapper .mdxeditor-toolbar [role="separator"] {
              flex-shrink: 0 !important;
              margin: 0 4px !important;
            }

            .unified-markdown-renderer-wrapper .mdxeditor-rich-text-editor {
              font-size: 16px !important; /* Предотвращение зума на iOS */
              background-color: hsl(var(--background)) !important;
              height: auto !important;
              overflow-y: auto !important;
            }
          }

          /* Placeholder стили */
          .unified-markdown-renderer-wrapper .mdxeditor-rich-text-editor[data-placeholder]:empty::before {
            content: attr(data-placeholder);
            color: hsl(var(--muted-foreground)) !important;
            font-style: italic;
            pointer-events: none;
          }
        `}</style>
      </div>
    );
  }
);

UnifiedMarkdownRenderer.displayName = "UnifiedMarkdownRenderer";